import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { focusRing } from '../styles/focus-ring';
import { typographyVariant } from '../typography';

import { collapsibleCardVariants } from './collapsible-card.styles';
import type {
  CollapsibleCardContentProps,
  CollapsibleCardCountProps,
  CollapsibleCardProps,
  CollapsibleCardTitleProps,
  CollapsibleCardTriggerProps,
} from './collapsible-card.types';

const CollapsibleCard = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Root>,
  CollapsibleCardProps
>(({ className, variant, ...props }, ref) => (
  <CollapsiblePrimitive.Root
    ref={ref}
    className={cn(collapsibleCardVariants({ variant }), className)}
    {...props}
  />
));
CollapsibleCard.displayName = 'CollapsibleCard';

const CollapsibleCardTrigger = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Trigger>,
  CollapsibleCardTriggerProps
>(({ className, children, actions, ...props }, ref) => {
  const labelId = React.useId();

  // `actions === undefined` (prop omitted) → the plain single-button branch,
  // where children render literally inside the Radix `<button>`. Passing
  // `actions={null}` explicitly opts into the overlay/grid branch below with
  // no visible action node — for headers whose only interactive content lives
  // inside `children` itself (e.g. a breadcrumb with clickable parent
  // segments, which must not be nested inside the trigger `<button>`). Those
  // descendants set their own `pointer-events-auto` to override the title
  // span's `pointer-events-none` and catch their own clicks.
  if (actions === undefined) {
    return (
      <CollapsiblePrimitive.Trigger
        ref={ref}
        className={cn(
          'group relative flex w-full items-center gap-2 rounded-lg text-start',
          // Hit-area expansion: stretches the click target back over the container's
          // px-3.5 / py-3 inset on all sides so the click target reaches the card
          // chrome's outer edges symmetrically.
          'before:absolute before:-inset-x-3.5 before:-inset-y-3 before:content-[""]',
          'text-text-secondary transition-colors duration-200 ease-out-cubic motion-reduce:transition-none',
          'enabled:hover:text-text-primary enabled:focus-visible:text-text-primary',
          focusRing,
          'disabled:cursor-not-allowed disabled:opacity-50',
          '[&_svg:not([class*=size-])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown
          aria-hidden
          className={cn(
            'ms-auto transition-transform duration-200 ease-out-cubic motion-reduce:transition-none',
            // Keyed on `aria-expanded` (not Radix's `data-state`) so the
            // rotation survives consumers wrapping the trigger in primitives
            // that also write `data-state` to it (e.g. Radix Tooltip's
            // `asChild`). `aria-expanded` is owned by Collapsible alone.
            'group-aria-[expanded=false]:-rotate-90'
          )}
        />
      </CollapsiblePrimitive.Trigger>
    );
  }

  // With actions: a single full-width Radix Trigger covers the entire row
  // (handles all toggling). Visible content sits on top via CSS Grid, with
  // title + chevron set to `pointer-events-none` so clicks/hover fall through
  // to the trigger underneath. Actions are real DOM siblings of the trigger
  // (no nested-button HTML), and they catch their own clicks because they
  // stack above the trigger via z-index.
  //
  // `className` is forwarded to the outer wrapper (the visible row) so
  // consumer styling targets what they see, mirroring the no-actions branch.
  return (
    <div
      className={cn(
        'relative grid w-full grid-cols-[auto_auto_1fr_auto] items-center gap-2 rounded-lg',
        className
      )}
    >
      <CollapsiblePrimitive.Trigger
        ref={ref}
        aria-labelledby={labelId}
        className={cn(
          'peer absolute inset-0 rounded-lg',
          focusRing,
          'disabled:cursor-not-allowed',
          // Hit-area expansion to the card's chrome edges, same as the
          // no-actions trigger.
          'before:absolute before:-inset-x-3.5 before:-inset-y-3 before:content-[""]'
        )}
        {...props}
      />

      <span
        id={labelId}
        className={cn(
          // min-w-0 lets the title cell shrink below its content so a long
          // title can truncate instead of overflowing the grid track.
          'pointer-events-none relative z-10 col-start-1 flex min-w-0 items-center gap-2',
          'text-text-secondary transition-colors duration-200 ease-out-cubic motion-reduce:transition-none',
          'peer-enabled:peer-hover:text-text-primary peer-enabled:peer-focus-visible:text-text-primary',
          'peer-disabled:opacity-50',
          '[&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0'
        )}
      >
        {children}
      </span>

      <div className="relative z-10 col-start-2 flex items-center gap-1">{actions}</div>

      <ChevronDown
        aria-hidden
        className={cn(
          'pointer-events-none relative z-10 col-start-4 size-4 shrink-0',
          'text-text-secondary transition-all duration-200 ease-out-cubic motion-reduce:transition-none',
          'peer-enabled:peer-hover:text-text-primary peer-disabled:opacity-50',
          // `aria-expanded` (not `data-state`) so consumers wrapping the
          // trigger in primitives that also write `data-state` (e.g. Radix
          // Tooltip's `asChild`) don't stomp the rotation rule mid-hover.
          'peer-aria-[expanded=false]:-rotate-90'
        )}
      />
    </div>
  );
});
CollapsibleCardTrigger.displayName = 'CollapsibleCardTrigger';

const CollapsibleCardTitle = React.forwardRef<HTMLSpanElement, CollapsibleCardTitleProps>(
  ({ className, truncate, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        typographyVariant({ variant: 'p2Bold' }),
        // Default: balanced multi-line wrap (NudgeCard etc. rely on this).
        // `truncate` opts into single-line + ellipsis for variable-length user
        // data. `min-w-0` is required to defeat the flex-item `min-width: auto`
        // default — without it the nowrap title pins the row at min-content
        // width and overflows instead of clipping.
        truncate
          ? 'min-w-0 truncate text-text-primary'
          : 'text-balance text-text-primary',
        className
      )}
      {...props}
    />
  )
);
CollapsibleCardTitle.displayName = 'CollapsibleCardTitle';

// A de-emphasised count rendered next to the title (e.g. "Tasks 3"). Uses the
// regular-weight `p2` variant + `text-text-secondary` so the number reads as
// secondary trim against the bold, full-contrast title — it stays secondary
// even on header hover (it sets its own colour, so it doesn't pick up the
// trigger's secondary→primary affordance). `-ms-1` tightens the title→count
// gap without changing the trigger's `gap-2` (so icon+title rows keep 8px).
// Non-interactive, so it's safe to render directly inside `CollapsibleCardTrigger`.
const CollapsibleCardCount = React.forwardRef<HTMLSpanElement, CollapsibleCardCountProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        typographyVariant({ variant: 'p2' }),
        '-ms-1 text-text-secondary',
        className
      )}
      {...props}
    />
  )
);
CollapsibleCardCount.displayName = 'CollapsibleCardCount';

const CollapsibleCardContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Content>,
  CollapsibleCardContentProps
>(({ className, children, ...props }, ref) => (
  <CollapsiblePrimitive.Content
    ref={ref}
    className={cn(
      // -mx-3.5 widens the Content box to the card's outer chrome so children
      // can sit closer to the edges than the card's px-3.5 inset would
      // otherwise allow, without being clipped by overflow-hidden.
      '-mx-3.5 overflow-hidden',
      // pb-1/-mb-1: give the last row's outset focus ring room inside the
      // overflow-hidden clip, then reclaim it so the visible gap is unchanged.
      '-mb-1 pb-1',
      // Row labels use body weight; the bold title belongs to the trigger.
      // Descendant selector lands at (0,2,0) — one rung above ItemTitle's
      // baked-in `font-medium` (0,1,0), so the default actually applies.
      '[&_[data-slot=item-title]]:font-normal',
      // Gate both animations behind motion-safe so users who prefer reduced
      // motion get a hard cut instead of a height + transform animation.
      'motion-safe:data-[state=closed]:animate-collapsible-up motion-safe:data-[state=open]:animate-collapsible-down',
      // Lock onto a permanent compositor layer so the keyframe's opacity +
      // transform don't promote it on animation start and demote it on end.
      // Without this, text re-rasterizes (GPU grayscale → CPU subpixel) at the
      // final frame, producing a visible glyph-weight flash.
      'motion-safe:will-change-transform',
      className
    )}
    {...props}
  >
    {/*
      px-1 sets a 4px gutter so a size=sm <Item> child (its own px-2.5 = 10px)
      lands its content at 4 + 10 = 14px from the card's outer edge — inline
      with the trigger title (which sits at the card's px-3.5 = 14px inset).
      pt-1 sets a 4px gap between the trigger and the first list item.
    */}
    <div className="px-1 pt-1">{children}</div>
  </CollapsiblePrimitive.Content>
));
CollapsibleCardContent.displayName = 'CollapsibleCardContent';

export {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardCount,
  CollapsibleCardTitle,
  CollapsibleCardTrigger,
  collapsibleCardVariants,
};
